import { useCallback, useEffect, useState } from "react";
import {
  Archive,
  Check,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  Pencil,
  Repeat,
  RotateCcw,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";

const STATES = [
  { key: "pending", label: "Awaiting review" },
  { key: "live", label: "Live" },
  { key: "retired", label: "Retired" },
  { key: "rejected", label: "Rejected" },
];

const COMPLEXITIES = [
  { key: "", label: "All" },
  { key: "simple", label: "Simple" },
  { key: "complex", label: "Complex" },
];

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

/** One region's faces, 20 a page: review, rename, retire, restore. */
const FaceLibraryBrowser = ({ region, regionLabel, focusState = "live", onChanged }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState(focusState);
  const [complexity, setComplexity] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const q = complexity ? `&complexity=${complexity}` : "";
    const res = await authFetch(
      `${API}/admin/faces/library?region=${region}&state=${state}&page=${page}${q}`
    );
    if (res.ok) {
      setData(await res.json());
      setSelected(new Set());
    }
    setLoading(false);
  }, [region, state, complexity, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [region, state, complexity]);

  useEffect(() => {
    setState(focusState);
  }, [focusState]);

  const faces = data?.faces || [];
  const counts = data?.state_counts || {};
  const allSelected = faces.length > 0 && selected.size === faces.length;

  const toggle = (faceId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(faceId)) next.delete(faceId);
      else next.add(faceId);
      return next;
    });

  const act = async (path, ids, done, extra = {}) => {
    if (ids.length === 0) {
      toast.error("Select some faces first");
      return;
    }
    const res = await authFetch(`${API}/admin/faces/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ face_ids: ids, ...extra }),
    });
    if (res.ok) {
      const body = await res.json();
      toast.success(done(body));
      load();
      onChanged?.();
    } else {
      const body = await res.json().catch(() => ({}));
      toast.error(body.detail || "That didn't work");
    }
  };

  const review = async (body, done) => {
    const res = await authFetch(`${API}/admin/faces/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      toast.success(done(await res.json()));
      load();
      onChanged?.();
    } else {
      toast.error("Review failed");
    }
  };

  const approve = (ids) => {
    if (ids.length === 0) {
      toast.error("Select some faces first");
      return;
    }
    review({ approve: ids }, (b) => `${b.approved} approved and live`);
  };

  const reject = (ids) => {
    if (ids.length === 0) {
      toast.error("Select some faces first");
      return;
    }
    review({ reject: ids }, (b) => `${b.rejected} rejected`);
  };

  const rejectRestApprove = () => {
    const rest = faces.filter((f) => !selected.has(f.face_id)).map((f) => f.face_id);
    review(
      { approve: rest, reject: [...selected] },
      (b) => `${b.approved} approved, ${b.rejected} rejected`
    );
  };

  const saveName = async () => {
    const res = await authFetch(`${API}/admin/faces/${editing.face_id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ first_name: editing.first, last_name: editing.last }),
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) {
      toast.success(`Renamed to ${body.first_name} ${body.last_name}`);
      setEditing(null);
      load();
    } else {
      toast.error(body.detail || "Rename failed");
    }
  };

  const stateLabel = STATES.find((s) => s.key === state)?.label.toLowerCase() || state;

  return (
    <div className="mb-6 p-4 bg-muted/30 border border-border" data-testid="face-library-browser">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="font-semibold" data-testid="face-library-heading">
          {regionLabel} library
        </p>
        <p className="text-sm text-muted-foreground" data-testid="face-library-count">
          {data ? (data.total === 0 ? "Nothing here" : `${data.from}–${data.to} of ${data.total}`) : "…"}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {STATES.map((s) => (
          <Button
            key={s.key}
            size="sm"
            variant={state === s.key ? "default" : "outline"}
            onClick={() => setState(s.key)}
            data-testid={`face-library-state-${s.key}`}
          >
            {s.label}
            {counts[s.key] != null ? ` (${counts[s.key]})` : ""}
          </Button>
        ))}
        <span className="w-px bg-border mx-1" />
        {COMPLEXITIES.map((c) => (
          <Button
            key={c.key || "all"}
            size="sm"
            variant={complexity === c.key ? "default" : "outline"}
            onClick={() => setComplexity(c.key)}
            data-testid={`face-library-complexity-${c.key || "all"}`}
          >
            {c.label}
          </Button>
        ))}
      </div>

      {faces.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelected(allSelected ? new Set() : new Set(faces.map((f) => f.face_id)))}
            data-testid="face-library-select-all"
          >
            <ListChecks className="w-4 h-4 mr-2" />
            {allSelected ? "Clear selection" : `Select all (${faces.length})`}
          </Button>

          {state === "pending" && (
            <>
              <Button
                size="sm"
                onClick={() => approve([...selected])}
                data-testid="face-library-approve-selected"
              >
                <Check className="w-4 h-4 mr-2" />
                Approve selected ({selected.size})
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => reject([...selected])}
                data-testid="face-library-reject-selected"
              >
                <X className="w-4 h-4 mr-2" />
                Reject selected
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={rejectRestApprove}
                data-testid="face-library-reject-rest"
              >
                Reject selected, approve the rest
              </Button>
              <p className="text-xs text-muted-foreground self-center">
                Nothing is ever approved automatically — approving puts a face live for trainees
                straight away.
              </p>
            </>
          )}

          {state === "retired" && (
            <Button
              size="sm"
              onClick={() => act("restore", [...selected], (b) => `${b.restored} put back live`)}
              data-testid="face-library-restore-selected"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Put back live ({selected.size})
            </Button>
          )}

          {state === "live" && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => act("retire", [...selected], (b) => `${b.retired} retired`)}
                data-testid="face-library-retire-selected"
              >
                <Archive className="w-4 h-4 mr-2" />
                Retire selected ({selected.size})
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  act(
                    "retire",
                    [...selected],
                    (b) =>
                      `${b.retired} retired · ${
                        b.replacements
                          .map((r) => r.selection.replace("Generating ", ""))
                          .join(", ") || "no replacements"
                      } on the way`,
                    { replace: true }
                  )
                }
                data-testid="face-library-retire-replace"
              >
                <Repeat className="w-4 h-4 mr-2" />
                Retire &amp; replace ({selected.size})
              </Button>
              <p className="text-xs text-muted-foreground self-center">
                Retiring takes a face out of circulation at once. It stays here under Retired and
                can be put back in one press. Past rounds and leaderboards are untouched.
              </p>
            </>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : faces.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="face-library-empty">
          No faces {stateLabel === "live" ? "live" : stateLabel} in {regionLabel}.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {faces.map((f) => (
            <div
              key={f.face_id}
              className={`border p-1 transition-colors ${
                selected.has(f.face_id) ? "border-primary bg-primary/10" : "border-border"
              }`}
              data-testid={`face-library-tile-${f.face_id}`}
            >
              <label className="flex items-center gap-2 mb-1 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={selected.has(f.face_id)}
                  onChange={() => toggle(f.face_id)}
                  className="accent-primary"
                  data-testid={`face-library-tile-select-${f.face_id}`}
                />
                <span className="text-muted-foreground">Select</span>
              </label>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${f.image_url}`}
                alt={`${f.first_name || ""} ${f.last_name || ""}`}
                className="w-full aspect-square object-cover"
                loading="lazy"
              />
              <div className="flex items-center justify-between gap-1 mt-1">
                <p className="text-xs font-semibold truncate">
                  {f.first_name} {f.last_name}
                </p>
                <button
                  onClick={() =>
                    setEditing({ face_id: f.face_id, first: f.first_name || "", last: f.last_name || "" })
                  }
                  className="text-muted-foreground hover:text-primary"
                  data-testid={`face-library-rename-${f.face_id}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
              </div>
              <div className="text-[10px] leading-tight text-muted-foreground">
                {f.region_label}
                {f.complexity_label ? ` · ${f.complexity_label}` : ""}
                <br />
                Added {formatDate(f.created_at)}
                <br />
                Seen by {f.times_seen} · {f.batch_id?.slice(0, 14) || "—"}
              </div>
              {f.lookalike_flagged && (
                <p
                  className="text-[10px] leading-tight text-amber-400 mt-1"
                  data-testid={`face-library-lookalike-${f.face_id}`}
                >
                  Possible look-alike of {f.lookalike_name || "someone live"} (
                  {Math.round(f.lookalike_score)}%)
                </p>
              )}
              {state === "pending" && (
                <div className="flex gap-1 mt-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 h-7 text-xs"
                    onClick={() => approve([f.face_id])}
                    data-testid={`face-library-approve-${f.face_id}`}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 h-7 text-xs"
                    onClick={() => reject([f.face_id])}
                    data-testid={`face-library-reject-${f.face_id}`}
                  >
                    Reject
                  </Button>
                </div>
              )}
              {state === "retired" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-full h-7 text-xs mt-1"
                  onClick={() => act("restore", [f.face_id], () => "Back in circulation")}
                  data-testid={`face-library-restore-${f.face_id}`}
                >
                  Put back live
                </Button>
              )}
              {state === "live" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-full h-7 text-xs mt-1"
                  onClick={() => act("retire", [f.face_id], () => "Retired")}
                  data-testid={`face-library-retire-${f.face_id}`}
                >
                  Retire
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {data && data.pages > 1 && (
        <div className="flex items-center gap-3 mt-4">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="face-library-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground" data-testid="face-library-page">
            Page {data.page} of {data.pages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= data.pages}
            onClick={() => setPage((p) => p + 1)}
            data-testid="face-library-next"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {editing && (
        <div className="mt-4 p-3 bg-background border border-primary flex flex-wrap items-end gap-2">
          <Input
            value={editing.first}
            onChange={(e) => setEditing({ ...editing, first: e.target.value })}
            placeholder="First name"
            className="w-36"
            data-testid="face-library-edit-first"
          />
          <Input
            value={editing.last}
            onChange={(e) => setEditing({ ...editing, last: e.target.value })}
            placeholder="Surname"
            className="w-36"
            data-testid="face-library-edit-last"
          />
          <Button size="sm" onClick={saveName} data-testid="face-library-edit-save">
            Save
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default FaceLibraryBrowser;
